<template>
  <AuthLayout>
    <template #form>
      <LoadingScreen v-if="googleLoading" color="var(--purple)" />

      <div v-else class="signup-form" data-cy="signup-page">
        <!-- Logo and Title -->
        <AuthFormHeader title="Create your account" subtitle="Sign up to start building amazing things." />

        <!-- Social Signup -->
        <div class="auth-social-section">
          <GoogleButton @success="onGoogleSignupSuccess" @failure="onGoogleSignupFailure" />

          <!-- Divider -->
          <div class="auth-divider">
            <div class="divider-line"></div>
            <span class="divider-text">or</span>
            <div class="divider-line"></div>
          </div>

          <!-- Signup Form -->
          <form @submit.prevent="signup" class="auth-form" novalidate>
            <!-- Name fields -->
            <div v-if="nameRequired" class="name-fields">
              <an-input
                label="First name"
                v-model="firstName"
                :invalid="!validFirstName"
                placeholder="First name"
                size="lg"
                :theme="theme"
                data-cy="signup-first-name"
                autocomplete="given-name"
              />
              <an-input
                label="Last name"
                v-model="lastName"
                :invalid="!validLastName"
                placeholder="Last name"
                size="lg"
                :theme="theme"
                data-cy="signup-last-name"
                autocomplete="family-name"
              />
              <div v-if="!validFirstName || !validLastName" class="auth-field-error" data-cy="signup-name-error">
                Umm.. we hope you're not a ghost
              </div>
            </div>

            <!-- Email -->
            <div>
              <an-input
                label="Email address"
                type="email"
                v-model="email"
                :invalid="!validEmail"
                placeholder="name@email.com"
                size="lg"
                :theme="theme"
                data-cy="signup-email"
                autocomplete="email"
              />
              <div v-if="errorMessage" class="auth-field-error" data-cy="signup-email-error">
                {{ errorMessage }}
              </div>
            </div>

            <!-- Password -->
            <div>
              <an-input
                label="Password"
                type="password"
                v-model="password"
                :invalid="!validPassword"
                placeholder="Password"
                size="lg"
                :theme="theme"
                data-cy="signup-password"
                autocomplete="new-password"
              />
              <div v-if="!validPassword" class="auth-field-error" data-cy="signup-password-error">
                Password must contain at least 8 characters
              </div>
            </div>

            <!-- Checkboxes -->
            <div class="signup-checkboxes">
              <an-checkbox class="auth-checkbox" v-model="acceptCommunication" variant="auth">
                <span>Join our email list for special offers and updates!</span>
              </an-checkbox>
              <an-checkbox class="auth-checkbox" v-model="agreeWithTos" variant="auth">
                <span>
                  I agree with Anima's
                  <a :href="privacyUrl" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  and
                  <a :href="termsUrl" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                </span>
              </an-checkbox>
            </div>

            <!-- Recover password button (if needed) -->
            <an-button
              v-if="showRecoverPasswordButton"
              @click="onRecoverClick"
              variant="secondary"
              size="lg"
              data-cy="signup-recover"
            >
              Recover password
            </an-button>

            <!-- Submit button -->
            <an-button
              type="submit"
              variant="purple"
              size="lg"
              :isWorking="loading"
              :rounded="false"
              :title="agreeWithTos ? '' : 'Accept Privacy Policy and Terms of Service to continue'"
              :disabled="!agreeWithTos"
              data-cy="signup-submit"
            >
              Create account
            </an-button>
          </form>
        </div>

        <!-- Links -->
        <div class="auth-links">
          <div class="auth-link-row">
            <span>Already have an account?</span>
            <an-link @click="$router.push({ name: 'login', query: $route.query })" class="underline-link">
              Log in
            </an-link>
          </div>
        </div>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import GoogleButton from '@/components/Button/GoogleButton';
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import { SignupMixin } from '@/mixins';

import auth from '@/auth';
import { isValidEmail, normalizeEmail } from '@/utils/email';
import { toastError } from '@/services/bus';
import { readCookie } from '@/utils/cookie';
import errorHandler from '@/services/errorHandler';
import { uuid } from '@/utils/uuid';
import { sendGoogleConversion, sendFacebookConversion } from '@/services/conversion';

export default {
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      errorMessage: '',
      theme: 'dark',
      validFirstName: true,
      validLastName: true,
      validEmail: true,
      validPassword: true,
      termsUrl: 'https://www.animaapp.com/tos/TOS.pdf',
      privacyUrl: 'https://support.animaapp.com/en/articles/8120546-anima-privacy-policy-explained',
      loading: false,
      googleLoading: false,
      showRecoverPasswordButton: false,
      acceptCommunication: false,
      agreeWithTos: false
    };
  },
  mounted() {
    this.applyTheme(this.theme, 'onboarding');
    this.onMounted();
  },
  beforeDestroy() {
    this.removeTheme();
  },
  components: {
    GoogleButton,
    LoadingScreen,
    AuthLayout,
    AuthFormHeader
  },
  mixins: [SignupMixin],
  computed: {
    ...mapState('projects', ['sharedProjects']),
    ...mapState('webappSystem', ['ipAddress']),
    ...mapState('devices', { currentDevice: 'currentItem' }),
    nameRequired() {
      return this.isDesktop;
    },
    ...mapGetters({
      isPricingExperimentScreenActive: 'experiments/isPricingExperimentScreenActive'
    })
  },
  methods: {
    ...mapActions({
      getUser: 'users/fetchOne',
      updateUser: 'users/update',
      createTeam: 'teams/create',
      fetchSharedProjects: 'projects/fetchSharedProjects'
    }),
    ...mapMutations({
      setCurrentUser: 'users/setCurrentItem',
      setCurrentTeam: 'teams/setCurrentItem'
    }),
    onMounted() {
      const { prefill } = this.$route.query;
      const email = readCookie('subscribed_email');
      if (prefill && isValidEmail(normalizeEmail(email))) {
        this.email = email;
      }
    },
    validateName() {
      this.validateFirstName();
      this.validateLastName();
    },
    validateFirstName() {
      this.validFirstName = !!this.firstName?.trim().length || !this.nameRequired;
    },
    validateLastName() {
      this.validLastName = !!this.lastName?.trim().length || !this.nameRequired;
    },
    validateEmail() {
      const email = normalizeEmail(this.email);
      this.validEmail = isValidEmail(email);
      if (!this.validEmail) {
        this.errorMessage = 'Please enter a valid email address';
      } else {
        this.errorMessage = '';
      }
    },
    validatePassword() {
      this.validPassword = this.password.length >= 8;
    },
    validateForm() {
      this.validateName();
      this.validateEmail();
      this.validatePassword();
      const isValid = this.validEmail && this.validFirstName && this.validLastName && this.validPassword;
      if (!isValid) {
        this.$trackEvent('signup.signup.invalid-form', {
          is_email_valid: this.validEmail,
          is_password_valid: this.validPassword,
          is_first_name_valid: this.validFirstName,
          is_last_name_valid: this.validLastName,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email
        });
      }
      return isValid;
    },
    async onGoogleSignupSuccess(googleUser) {
      try {
        this.googleLoading = true;
        await this.onGoogleSuccess({ ...googleUser, agreeWithTos: this.agreeWithTos });
        this.signupExperiments();
      } catch (error) {
        this.googleLoading = false;
        errorHandler.captureException(error);
        toastError("We couldn't log you in with your Google account :(");
      }
    },
    async onGoogleSignupFailure(err) {
      this.onGoogleFailure(err);
      toastError("We couldn't log you in with your Google account :(");
    },
    onRecoverClick() {
      this.$trackEvent('signup.recover-password.click');
      this.$router.replace({ name: 'forgot-password', query: { ...this.$route.query, email: this.email } });
    },
    async signup() {
      try {
        const { query } = this.$route;
        this.$trackEvent('signup.signup-button.click', query);
        this.errorMessage = '';

        if (!this.validateForm()) {
          return;
        }

        const email = normalizeEmail(this.email);
        const { firstName, lastName } = this;

        const transactionId = uuid();

        this.loading = true;
        const { access_token: token, ...user } = await auth.signup(
          firstName,
          lastName,
          email,
          this.password,
          this.acceptCommunication,
          this.agreeWithTos
        );
        localStorage.setItem('token', token);
        this.signupExperiments();
        this.setCurrentUser(user);

        this.signupTracking(user, transactionId);

        await this.fetchSharedProjects();
        setTimeout(() => {
          this.redirectAfterAuth();
          this.loading = false;
        }, 1000);
      } catch (error) {
        const validationErrors = [
          'Email exists. If this is you, please login',
          'Ummm. Is this a valid email you got there?',
          "Hmm.. We couldn't verify you're a human. Try refreshing the page?"
        ];

        this.errorMessage = error?.response?.data?.message || 'Failed to sign up';
        if (this.errorMessage === 'Email exists. If this is you, please login') {
          this.showRecoverPasswordButton = true;
        }

        if (!validationErrors.includes(this.errorMessage)) {
          errorHandler.captureException(this.errorMessage);
        }

        this.$trackEvent('signup.signup.failure', { cta: 'anima', message: this.errorMessage });
        this.loading = false;
      }
    },
    signupTracking(user, transactionId) {
      try {
        const { query } = this.$route;
        const email = normalizeEmail(this.email);
        const { firstName, lastName } = this;

        this.$trackEvent('signup.signup.success', {
          ...query,
          cta: 'anima',
          device_id: this.currentDevice.device_id,
          email: email
        });

        this.$gtm.trackEvent({
          event: 'sign_up',
          fbEventID: `CompleteRegistration:${user.id}`,
          fbIpAddress: this.ipAddress,
          fbUserAgent: navigator.userAgent,
          fbc: readCookie('_fbc'),
          fbp: readCookie('_fbp'),
          fbCurrency: 'USD',
          fbValue: 5,
          fbFirstName: firstName,
          fbLastName: lastName,
          event_category: 'Signup',
          event_action: `${firstName} ${lastName}`,
          event_label: email,
          transaction_id: transactionId,
          external_id: user?.id,
          first_name: user?.first_name,
          last_name: user?.last_name,
          email: user?.email
        });

        this.$tracking.setUserEmail(email);
        this.$tracking.setUserId(user.id);
        this.$tracking.alias(user.id);

        sendGoogleConversion('signup', transactionId, {
          email,
          first_name: user.first_name,
          last_name: user.last_name
        });

        sendFacebookConversion('CompleteRegistration', transactionId, {
          id: user.id,
          email,
          first_name: user.first_name,
          last_name: user.last_name
        });

        // NOTE: Tolt affiliate tracking is handled by the backend.
        // We pass tolt_partner_id in signup request, and backend creates
        // the Tolt customer with team.str_id as customer_id for payment tracking.
        // Do NOT use window.tolt.signup() - it would create a customer with
        // Tolt's generated ID, breaking payment correlation.
      } catch (err) {
        errorHandler.captureException(err);
      }
    },
    signupExperiments() {
      localStorage.setItem('pricingScreenFlowActive', this.isPricingExperimentScreenActive);
      this.$trackEvent('pricing::screen-tier-flow', { active: this.isPricingExperimentScreenActive });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

.signup-form {
  display: flex;
  flex-direction: column;
  width: 100%;

  > :first-child {
    margin-bottom: $auth-gap-xl;
  }

  > .auth-social-section {
    margin-bottom: $auth-gap-sm;
  }
}

// Signup-specific overrides for shared auth styles
.auth-form {
  .signup-checkboxes {
    margin-top: $auth-gap-xs; // Total gap of 24px (16px base + 8px)
  }
}

.name-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  grid-column: 1 / -1;

  @include mobile {
    grid-template-columns: 1fr;
  }

  .auth-field-error {
    grid-column: 1 / -1;
    margin-top: -8px; // Compensate for grid gap (16px - 8px = 8px total)
  }
}

.auth-field-error {
  margin-top: 8px;
  text-align: left;
  font-size: 14px;
  color: var(--red);
}

.signup-checkboxes {
  display: flex;
  flex-direction: column;
}

.auth-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;

  .auth-link-row {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--transparent-white-50);

    // Signup-specific primary link style
    .underline-link {
      color: var(--white) !important;
      font-family: $auth-body-font;
      font-size: $auth-text-size;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
      transition: color 0.2s ease;

      &:hover {
        color: var(--transparent-white-80) !important;
        font-weight: 400 !important;
      }
    }
  }
}
</style>
